/**
 * Asset Types API client
 */

import { apiClient } from './client';
import type { AssetType, AssetTypeCreateData, AssetTypeUpdateData } from '../../types/asset';

export const assetTypesApi = {
  /**
   * Create a new asset type
   */
  async create(companyId: string, data: AssetTypeCreateData): Promise<AssetType> {
    // Transform camelCase to snake_case for API
    const payload = {
      name: data.name,
      name_singular: data.nameSingular,
      fields: data.fields.map(field => {
        const fieldData: any = {
          field_name: field.fieldName,
          field_type: field.fieldType,
          required: field.required,
          is_primary_display: field.isPrimaryDisplay,
        };
        // Only include field_id if it exists and is not undefined
        if (field.fieldId !== undefined) {
          fieldData.field_id = field.fieldId;
        }
        return fieldData;
      }),
    };
    const response = await apiClient.post<AssetType>(
      `/api/v1/asset-types?company_id=${companyId}`,
      payload
    );
    return response.data;
  },

  /**
   * List asset types from a company
   */
  async list(companyId: string, activeOnly: boolean = true): Promise<AssetType[]> {
    const params = new URLSearchParams({ company_id: companyId });
    if (activeOnly) {
      params.append('active_only', 'true');
    }
    const response = await apiClient.get<AssetType[]>(`/api/v1/asset-types?${params.toString()}`);
    return response.data;
  },

  /**
   * Get asset type by ID
   */
  async getById(assetTypeId: string, companyId: string): Promise<AssetType> {
    const response = await apiClient.get<AssetType>(
      `/api/v1/asset-types/${assetTypeId}?company_id=${companyId}`
    );
    return response.data;
  },

  /**
   * Update asset type
   */
  async update(
    assetTypeId: string,
    companyId: string,
    data: AssetTypeUpdateData
  ): Promise<AssetType> {
    // Transform camelCase to snake_case for API
    const payload: any = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.nameSingular !== undefined) payload.name_singular = data.nameSingular;
    if (data.fields !== undefined) {
      payload.fields = data.fields.map(field => {
        const fieldData: any = {
          field_name: field.fieldName,
          field_type: field.fieldType,
          required: field.required,
          is_primary_display: field.isPrimaryDisplay,
        };
        // Only include field_id if it exists and is not undefined
        if (field.fieldId !== undefined) {
          fieldData.field_id = field.fieldId;
        }
        return fieldData;
      });
    }
    if (data.isActive !== undefined) payload.is_active = data.isActive;

    const response = await apiClient.put<AssetType>(
      `/api/v1/asset-types/${assetTypeId}?company_id=${companyId}`,
      payload
    );
    return response.data;
  },

  /**
   * Delete asset type
   */
  async delete(assetTypeId: string, companyId: string): Promise<void> {
    await apiClient.delete(`/api/v1/asset-types/${assetTypeId}?company_id=${companyId}`);
  },

  /**
   * Deactivate asset type
   */
  async deactivate(assetTypeId: string, companyId: string): Promise<void> {
    await apiClient.patch(`/api/v1/asset-types/${assetTypeId}/deactivate?company_id=${companyId}`);
  },
};
